/**
 * Microsoft Entra ID auth (MSAL v5). Production auth for Microsoft deployments;
 * Google Workspace deployments use auth/google/.
 */

import { InteractionRequiredAuthError, InteractionStatus, type IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { setTokenProvider } from "@/api";
import { getRuntimeConfig } from "@/config/runtime";
import { AuthContext, type AuthContextValue } from "../AuthContext";
import { buildApiScopes, buildLoginRequest } from "./msal";

export function MsalAuthProvider({
  instance,
  children,
}: Readonly<{ instance: IPublicClientApplication; children: ReactNode }>) {
  return (
    <MsalProvider instance={instance}>
      <MsalAuthBridge>{children}</MsalAuthBridge>
    </MsalProvider>
  );
}

function MsalAuthBridge({ children }: Readonly<{ children: ReactNode }>) {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    const scopes = buildApiScopes(getRuntimeConfig());
    if (!account || scopes.length === 0) return null;
    try {
      return (await instance.acquireTokenSilent({ scopes, account })).accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Navigates away; the redirect bridge returns the user here afterwards.
        await instance.acquireTokenRedirect({ scopes, account });
        return null;
      }
      throw error;
    }
  }, [instance]);

  useEffect(() => {
    setTokenProvider(getAccessToken);
    return () => setTokenProvider(null);
  }, [getAccessToken]);

  const login = useCallback(() => instance.loginRedirect(buildLoginRequest(getRuntimeConfig())), [instance]);
  const logout = useCallback(() => instance.logoutRedirect(), [instance]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading: inProgress !== InteractionStatus.None,
      accountName: accounts[0]?.name ?? null,
      login,
      logout,
    }),
    [isAuthenticated, inProgress, accounts, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
